// @flow

import _ from 'lodash';
import { rem } from 'polished';
import React, { Component } from 'react';
import styled from 'styled-components';

import Button from 'client/components/button/button';
import COLOR from 'client/constants/colors';
import { PRE_REG_ACCOUNT_CREATE } from 'client/constants/amplitudeTracker';

import { Tracking } from 'web-core/src/tracking';

import AccountCreate from './accountCreate';
import Complete from './complete';
import OepCountdown from './oepCountdown';
import RegistrationForm from './registrationForm';
import Start from './start';
import TextReminder from './textReminder';
import WelcomeBack from './welcome';

export const VIEW_TYPE = {
  ACCOUNT_CREATE: 'ACCOUNT_CREATE',
  COMPLETE: 'COMPLETE',
  OEP_COUNTDOWN: 'OEP_COUNTDOWN',
  REGISTRATION_FORM: 'REGISTRATION_FORM',
  START: 'START',
  TEXT_REMINDER: 'TEXT_REMINDER',
  WELCOME_BACK: 'WELCOME_BACK',
};

export const FORM_FIELD_NAME = {
  CONFIRMED: 'confirmed',
  EMAIL: 'email',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  PASSWORD: 'password',
  PHONE_NUMBER: 'phone_number',
  ZIPCODE: 'zipcode',
};

type ViewTypes =
  | 'ACCOUNT_CREATE'
  | 'COMPLETE'
  | 'OEP_COUNTDOWN'
  | 'REGISTRATION_FORM'
  | 'START'
  | 'TEXT_REMINDER'
  | 'WELCOME_BACK';

type Props = {
  commonTranslations: TranslationType,
  contentfulData: Object,
  createWithResources: Function,
  firstName: string,
  formData: Object,
  getAddressFromZip: Function,
  isLoggedIn: boolean,
  isRegistered: boolean,
  isValidForm: boolean,
  isEmptyCart: boolean,
  memberDashboard: boolean,
  oepStartDate: string,
  postEmailSubscription: Function,
  preregistration: Object,
  primaryMemberId: string,
  phase3Enabled: boolean,
};

type State = {
  view: ViewTypes,
  isSubmitting: boolean,
  submitError: Object | null,
};

const PreRegistrationWrapper = styled.div`
  text-align: left;
  max-width: ${rem(450)};
`;

export const ButtonWrapper = styled(Button)`
  background-color: ${COLOR.BLUE_0};
  margin: 1rem 0;
`;

const initialState = {
  isSubmitting: false,
  submitError: null,
};

const getInitialView = (props) => {
  const {
    isLoggedIn,
    contentfulData: { oepStartDate },
  } = props;
  const today = Date.now();
  const oepStart = new Date(oepStartDate);

  if (today > oepStart) {
    return VIEW_TYPE.OEP_COUNTDOWN;
  } else if (isLoggedIn) {
    return VIEW_TYPE.WELCOME_BACK;
  }
  return VIEW_TYPE.START;
};

class PreRegistration extends Component<Props, State> {
  state = {
    view: getInitialView(this.props),
    ...initialState,
  };

  setView = (step: ViewTypes) => {
    this.setState({
      view: step,
      ...initialState,
    });
  };

  handleAccountCreate = () => {
    const {
      contentfulData: { sourceName, subscriptionName },
      createWithResources,
      formData,
      getAddressFromZip,
      postEmailSubscription,
      primaryMemberId,
    } = this.props;
    const confirmed = formData[FORM_FIELD_NAME.CONFIRMED];
    const email = formData[FORM_FIELD_NAME.EMAIL];
    const first_name = formData[FORM_FIELD_NAME.FIRST_NAME];
    const last_name = formData[FORM_FIELD_NAME.LAST_NAME];
    const password = formData[FORM_FIELD_NAME.PASSWORD];
    const phone_number = formData[FORM_FIELD_NAME.PHONE_NUMBER];
    const zipcode = formData[FORM_FIELD_NAME.ZIPCODE];

    this.setState({
      isSubmitting: true,
      submitError: false,
    });
    return getAddressFromZip(zipcode)
      .then(([address]) => {
        if (_.isEmpty(address)) {
          throw new Error(`Could not find address with zip code: ${zipcode}`);
        }
        return address;
      })
      .then(address =>
        createWithResources({
          apps: [],
          address: {
            ...address,
            phone_number: confirmed ? phone_number : null,
          },
          email,
          members: [
            {
              id: primaryMemberId,
              type: 'primary',
              first_name,
              last_name,
            },
          ],
          password,
          source: sourceName,
        }))
      .then(() => {
        if (confirmed && phone_number) {
          const params = {
            email,
            lists: [subscriptionName],
          };
          return postEmailSubscription({ params });
        }
        return null;
      })
      .then(() => {
        Tracking.track(PRE_REG_ACCOUNT_CREATE, { status: 'success' });
        this.setState({ ...initialState });
        this.setView(VIEW_TYPE.COMPLETE);
      })
      .catch((error) => {
        this.setState({
          isSubmitting: false,
          submitError: error,
        });
      });
  };

  render() {
    const { view, isSubmitting, submitError } = this.state;
    const {
      commonTranslations,
      contentfulData,
      contentfulData: { oepEndDate, oepStartDate },
      firstName,
      formData,
      isValidForm,
      isEmptyCart,
      phase3Enabled,
      memberDashboard,
    } = this.props;

    const confirmed = _.get(formData, FORM_FIELD_NAME.CONFIRMED, false);
    const phoneNumber = _.get(formData, FORM_FIELD_NAME.PHONE_NUMBER, null);

    let DisplayView;
    switch (view) {
      case VIEW_TYPE.START:
        DisplayView = <Start setView={this.setView} {...contentfulData[VIEW_TYPE.START]} />;
        break;

      case VIEW_TYPE.WELCOME_BACK:
        DisplayView = (
          <WelcomeBack
            isEmptyCart={isEmptyCart}
            oepStartDate={oepStartDate}
            setView={this.setView}
            firstName={firstName}
            phase3={phase3Enabled}
            memberDashboard={memberDashboard}
            {...contentfulData[VIEW_TYPE.WELCOME_BACK]} />
        );
        break;

      case VIEW_TYPE.OEP_COUNTDOWN:
        DisplayView = (
          <OepCountdown
            isEmptyCart={isEmptyCart}
            oepEndDate={oepEndDate}
            setView={this.setView}
            {...contentfulData[VIEW_TYPE.OEP_COUNTDOWN]} />
        );
        break;

      case VIEW_TYPE.REGISTRATION_FORM:
        DisplayView = (
          <RegistrationForm
            commonTranslations={commonTranslations}
            isValid={isValidForm}
            setView={this.setView}
            {...contentfulData[VIEW_TYPE.REGISTRATION_FORM]} />
        );
        break;

      case VIEW_TYPE.TEXT_REMINDER:
        DisplayView = (
          <TextReminder
            commonTranslations={commonTranslations}
            isValid={isValidForm}
            setView={this.setView}
            {...contentfulData[VIEW_TYPE.TEXT_REMINDER]} />
        );
        break;

      case VIEW_TYPE.ACCOUNT_CREATE:
        DisplayView = (
          <AccountCreate
            previousView={
              confirmed && phoneNumber ? VIEW_TYPE.TEXT_REMINDER : VIEW_TYPE.REGISTRATION_FORM
            }
            isSubmitting={isSubmitting}
            isValid={isValidForm}
            onCreate={this.handleAccountCreate}
            setView={this.setView}
            submitError={submitError}
            {...contentfulData[VIEW_TYPE.ACCOUNT_CREATE]} />
        );
        break;

      case VIEW_TYPE.COMPLETE:
        DisplayView = <Complete setView={this.setView} {...contentfulData[VIEW_TYPE.COMPLETE]} />;
        break;

      default:
        DisplayView = <Start setView={this.setView} {...contentfulData[VIEW_TYPE.START]} />;
        break;
    }

    return <PreRegistrationWrapper>{DisplayView}</PreRegistrationWrapper>;
  }
}

export default PreRegistration;
